import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, BaseDialog, Button, FormProvider, Stack, useForm } from '@metarouter-private/mr-mui';

import { useConsentModeEnabled } from 'app/components/consent-mode-select/GatedConsentModeSelect';
import { useConsentCategorySelectEnabled } from './shared/consent-category/GatedConsentCategorySelect';

import ConfirmUpdateContent from './ConfirmUpdateContent';
import ConfirmUpdateActions from './ConfirmUpdateActions';
import useConfirmUpdateModal from './useConfirmUpdateModal';
import {
  getRequiredVariableNames,
  buildConfirmUpdateDefaultValues,
  makeConfirmUpdateResolver,
} from './confirmUpdateFormModel';

export default function ConfirmUpdateModal({ onSuccess }) {
  const {
    isOpen,
    onClose,
    selectedPipelines,
    selectedIntegration,
    targetPlaybook,
    integrationId,
    handleSubmit,
    isSubmitting,
    progress,
  } = useConfirmUpdateModal({ onSuccess });

  const { consentModeEnabled } = useConsentModeEnabled();
  const { consentCategorySelectEnabled: consentCategoryEnabled } = useConsentCategorySelectEnabled();

  // Validation lives in a resolver over the WHOLE selection (seeded into
  // defaultValues), not in per-field rules — so isValid stays correct for
  // pipelines whose rows aren't mounted, the prerequisite for paginating /
  // virtualizing the list. A resolver supersedes all field-level rules, so it
  // owns consent validation too (see confirmUpdateFormModel).
  const requiredVariableNames = useMemo(() => getRequiredVariableNames(targetPlaybook), [targetPlaybook]);

  const defaultValues = useMemo(
    () =>
      buildConfirmUpdateDefaultValues({
        selectedPipelines,
        targetPlaybook,
        integrationId,
        consentModeEnabled,
        consentCategoryEnabled,
      }),
    [selectedPipelines, targetPlaybook, integrationId, consentModeEnabled, consentCategoryEnabled]
  );

  const resolver = useMemo(
    () =>
      makeConfirmUpdateResolver({
        pipelineIds: selectedPipelines.map(pipeline => pipeline.id),
        requiredVariableNames,
        consentCategoryEnabled,
      }),
    [selectedPipelines, requiredVariableNames, consentCategoryEnabled]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues,
    resolver,
  });
  const { reset } = methods;

  // useForm seeds defaultValues only at its keyed mount, before the selection is
  // final — re-seed so never-expanded rows aren't absent from the form. Safe for
  // in-progress edits: defaultValues is stable while the dialog is open (nothing
  // refetches `pipelines` mid-edit).
  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // Once rows collapse + paginate, a failed submit can set errors only on
  // off-page pipelines with nothing visible — the button would look broken.
  // incompleteCount is the count from the last failed submit (only ever set > 0
  // by onInvalid), so it doubles as the banner's visibility flag.
  // Declared before the early return to keep hook order stable.
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);

  // The modal isn't unmounted on close (key={selectedPlaybookId} only remounts
  // on a playbook change), so reset the banner state when it transitions to
  // closed — otherwise a stale warning shows on reopening the same playbook.
  useEffect(() => {
    if (!isOpen) {
      setIncompleteCount(0);
      setShowOnlyIncomplete(false);
    }
  }, [isOpen]);

  if (!isOpen || !targetPlaybook) {
    return null;
  }

  const onFormSubmit = data => {
    handleSubmit(data);
  };

  // The resolver keys its errors by pipelineId, so the number of error keys is
  // exactly the number of incomplete pipelines — no getValues snapshot needed.
  const onInvalid = errors => {
    setIncompleteCount(Object.keys(errors).length);
  };

  const showIncompleteBanner = incompleteCount > 0;

  return (
    <BaseDialog
      DialogProps={{
        open: isOpen,
        onClose,
        fullWidth: true,
        maxWidth: 'lg',
      }}
      Container={FormProvider}
      ContainerProps={methods}
      title={`Update to ${selectedIntegration?.name} - Revision ${targetPlaybook.revision}`}
      content={
        <Stack spacing={2}>
          {showIncompleteBanner && (
            <Alert
              severity="warning"
              data-test-id="incompleteSubmitWarning"
              action={
                <Button color="inherit" size="small" onClick={() => setShowOnlyIncomplete(true)}>
                  Show only these
                </Button>
              }
            >
              {`${incompleteCount} pipeline${incompleteCount === 1 ? '' : 's'} ${
                incompleteCount === 1 ? 'is' : 'are'
              } missing required values and can't be updated yet.`}
            </Alert>
          )}
          <ConfirmUpdateContent
            selectedPipelines={selectedPipelines}
            targetPlaybook={targetPlaybook}
            integrationId={integrationId}
            pipelineCountMessage={`${selectedPipelines.length} pipeline${
              selectedPipelines.length !== 1 ? 's' : ''
            } selected`}
            showOnlyIncomplete={showOnlyIncomplete}
            onToggleShowOnlyIncomplete={setShowOnlyIncomplete}
          />
        </Stack>
      }
      actions={
        <ConfirmUpdateActions
          onClose={onClose}
          onSubmit={methods.handleSubmit(onFormSubmit, onInvalid)}
          isSubmitting={isSubmitting}
          progress={progress}
        />
      }
    />
  );
}

ConfirmUpdateModal.propTypes = {
  onSuccess: PropTypes.func,
};

ConfirmUpdateModal.defaultProps = {
  onSuccess: null,
};
